import { create } from 'zustand';
import type {
  // Unified types
  Item,
  SubItem,
  TaskItem,
  TransactionItem,
  NoteItem,
  KeyDateItem,
  CreateItemInput,
  UpdateItemInput,
  CreateSubItemInput,
  UpdateSubItemInput,
  Attachment,
  Reminder,
  UserRole,
} from '@perfin/shared';

// Unified service
import {
  subscribeItems,
  subscribeSubItems,
  createItem,
  updateItem,
  deleteItem,
  createSubItem,
  updateSubItem,
  deleteSubItem,
  reorderSubItems,
  reorderItemChildren,
  uploadItemAttachment,
  deleteItemAttachment,
  uploadSubItemAttachment,
  deleteSubItemAttachment,
  addReminder as addReminderService,
  removeReminder as removeReminderService,
} from '@perfin/shared';

// ============================================================================
// STORE INTERFACE
// ============================================================================

interface ItemState {
  // ========== UNIFIED STATE ==========
  items: Item[];
  subItemsMap: Record<string, SubItem[]>; // Keyed by parentId
  subscribedUserId: string | null; // Track active subscription for idempotency

  // Computed selectors (call as functions)
  getTasks: () => TaskItem[];
  getTransactions: () => TransactionItem[];
  getNotes: () => NoteItem[];
  getKeyDates: () => KeyDateItem[];

  // ========== COMMON STATE ==========
  loading: boolean;
  error: string | null;

  // ========== UNIFIED ACTIONS ==========
  subscribeToAllItems: (userId: string, orgId: string, role: UserRole) => () => void;
  subscribeToItemSubItems: (userId: string, parentId: string) => () => void;

  addItem: (userId: string, input: CreateItemInput) => Promise<string>;
  editItem: (userId: string, itemId: string, input: UpdateItemInput) => Promise<void>;
  removeItem: (userId: string, itemId: string) => Promise<void>;

  addSubItem: (userId: string, parentId: string, input: CreateSubItemInput) => Promise<string>;
  editSubItem: (userId: string, parentId: string, subItemId: string, input: UpdateSubItemInput) => Promise<void>;
  removeSubItem: (userId: string, parentId: string, subItemId: string) => Promise<void>;
  reorderSubItems: (userId: string, parentId: string, orderedIds: string[]) => Promise<void>;
  reorderItemChildren: (userId: string, itemId: string, orderedItems: Array<{ type: 'attachment' | 'subItem'; id: string }>) => Promise<void>;

  addItemAttachment: (userId: string, itemId: string, file: File, orgId: string) => Promise<Attachment>;
  removeItemAttachment: (userId: string, itemId: string, attachment: Attachment) => Promise<void>;
  addSubItemAttachment: (userId: string, parentId: string, subItemId: string, file: File, orgId: string) => Promise<Attachment>;
  removeSubItemAttachment: (userId: string, parentId: string, subItemId: string, attachment: Attachment) => Promise<void>;

  // Reminder actions
  addReminder: (userId: string, itemId: string, reminder: Omit<Reminder, 'id'>, subItemId?: string) => Promise<Reminder>;
  removeReminder: (userId: string, itemId: string, reminderId: string, subItemId?: string) => Promise<void>;

  // Utilities
  clearError: () => void;
  clearSubscriptions: () => void;
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

export const useItemStore = create<ItemState>((set, get) => ({
  // ========== UNIFIED STATE ==========
  items: [],
  subItemsMap: {},
  subscribedUserId: null,

  // Computed selectors
  getTasks: () => get().items.filter((i): i is TaskItem => i.type === 'task'),
  getTransactions: () => get().items.filter((i): i is TransactionItem => i.type === 'transaction'),
  getNotes: () => get().items.filter((i): i is NoteItem => i.type === 'note'),
  getKeyDates: () => get().items.filter((i): i is KeyDateItem => i.type === 'keyDate'),

  // ========== COMMON STATE ==========
  loading: false,
  error: null,

  // ========== UNIFIED ACTIONS ==========
  subscribeToAllItems: (userId: string, orgId: string, role: UserRole) => {
    // Idempotency: prevent duplicate subscriptions for same user
    if (get().subscribedUserId === userId) {
      return () => {}; // Already subscribed, return no-op
    }
    set({ subscribedUserId: userId, loading: true });
    return subscribeItems(userId, orgId, role, (items) => set({ items, loading: false }));
  },

  subscribeToItemSubItems: (userId: string, parentId: string) => {
    return subscribeSubItems(userId, parentId, (subItemList) => {
      set((state) => ({
        subItemsMap: { ...state.subItemsMap, [parentId]: subItemList },
      }));
    });
  },

  addItem: async (userId, input) => {
    set({ loading: true, error: null });
    try {
      const id = await createItem(userId, input);
      set({ loading: false });
      return id;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create item';
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  editItem: async (userId, itemId, input) => {
    set({ loading: true, error: null });
    try {
      await updateItem(userId, itemId, input);
      set({ loading: false });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update item';
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  removeItem: async (userId, itemId) => {
    set({ loading: true, error: null });
    try {
      await deleteItem(userId, itemId);
      set({ loading: false });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete item';
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  addSubItem: async (userId, parentId, input) => {
    try {
      return await createSubItem(userId, parentId, input);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create sub-item';
      set({ error: errorMessage });
      throw error;
    }
  },

  editSubItem: async (userId, parentId, subItemId, input) => {
    try {
      await updateSubItem(userId, parentId, subItemId, input);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update sub-item';
      set({ error: errorMessage });
      throw error;
    }
  },

  removeSubItem: async (userId, parentId, subItemId) => {
    try {
      await deleteSubItem(userId, parentId, subItemId);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete sub-item';
      set({ error: errorMessage });
      throw error;
    }
  },

  reorderSubItems: async (userId, parentId, orderedIds) => {
    try {
      await reorderSubItems(userId, parentId, orderedIds);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reorder sub-items';
      set({ error: errorMessage });
      throw error;
    }
  },

  reorderItemChildren: async (userId, itemId, orderedItems) => {
    try {
      await reorderItemChildren(userId, itemId, orderedItems);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reorder items';
      set({ error: errorMessage });
      throw error;
    }
  },

  addItemAttachment: async (userId, itemId, file, orgId) => {
    try {
      return await uploadItemAttachment(userId, itemId, file, orgId);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload attachment';
      set({ error: errorMessage });
      throw error;
    }
  },

  removeItemAttachment: async (userId, itemId, attachment) => {
    try {
      await deleteItemAttachment(userId, itemId, attachment);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete attachment';
      set({ error: errorMessage });
      throw error;
    }
  },

  addSubItemAttachment: async (userId, parentId, subItemId, file, orgId) => {
    try {
      return await uploadSubItemAttachment(userId, parentId, subItemId, file, orgId);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload attachment';
      set({ error: errorMessage });
      throw error;
    }
  },

  removeSubItemAttachment: async (userId, parentId, subItemId, attachment) => {
    try {
      await deleteSubItemAttachment(userId, parentId, subItemId, attachment);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete attachment';
      set({ error: errorMessage });
      throw error;
    }
  },

  // Reminder actions
  addReminder: async (userId, itemId, reminder, subItemId) => {
    try {
      return await addReminderService(userId, itemId, reminder, subItemId);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add reminder';
      set({ error: errorMessage });
      throw error;
    }
  },

  removeReminder: async (userId, itemId, reminderId, subItemId) => {
    try {
      await removeReminderService(userId, itemId, reminderId, subItemId);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to remove reminder';
      set({ error: errorMessage });
      throw error;
    }
  },

  clearError: () => set({ error: null }),

  // Clear all subscriptions and reset state (called on logout)
  clearSubscriptions: () => set({
    items: [],
    subItemsMap: {},
    subscribedUserId: null,
    loading: false,
    error: null,
  }),
}));
